import { c as _c } from "react/compiler-runtime";
import { deepOmit } from "@website/utils/deep-omit";
import { isFilled } from "@website/utils/is-filled";
import { CheckboxFilter } from "@website/widget/filter/filter-components/checkbox";
import { RadioFilter } from "@website/widget/filter/filter-components/radio";
import { RangeFilter } from "@website/widget/filter/filter-components/range";
import { HotelFilterChipsPropertiesTypes } from "@website/widget/filter/hotel-filter-chips/hotel-filter-chips.types";
import omit from "lodash-es/omit";
import { useRouter } from "next/router";
import { MouseEvent, useState } from "react";
type DraftRangeValues = Record<string, [number, number]>;
export const useHotelFiltersChips = t0 => {
  const $ = _c(26);
  const {
    chipsFiltersSchema,
    hidePopoverActions,
    deferRangeUpdates
  } = t0;
  const router = useRouter();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [draftRangeValues, setDraftRangeValues] = useState(t1);
  const shouldDeferRangeUpdates = deferRangeUpdates ?? !hidePopoverActions;
  let t2;
  if ($[1] !== chipsFiltersSchema) {
    t2 = Object.entries(chipsFiltersSchema);
    $[1] = chipsFiltersSchema;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const filtersSchemaEntries = t2;
  let t3;
  if ($[3] !== filtersSchemaEntries || $[4] !== shouldDeferRangeUpdates) {
    t3 = filtersSchemaEntries.map(t4 => {
      const [name, schema] = t4;
      switch (schema.type) {
        case "range":
          {
            return <RangeFilter key={name} {...schema} onValueChange={shouldDeferRangeUpdates ? value => setDraftRangeValues(previous => ({
              ...previous,
              [name]: value
            })) : undefined} showTitle={false} updateQueryOnChange={!shouldDeferRangeUpdates} />;
          }
        case "checkbox":
          {
            return <CheckboxFilter collapseClassName="!overflow-auto" containerClassName={schema?.containerClassName} key={name} name={name} {...schema} isNew={false} showTitle={false} />;
          }
        case "radio":
          {
            return <RadioFilter key={name} name={name} {...schema} isNew={false} showTitle={false} />;
          }
        default:
          {
            return null;
          }
      }
    }).filter(Boolean);
    $[3] = filtersSchemaEntries;
    $[4] = shouldDeferRangeUpdates;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const children = t3;
  let t4;
  if ($[6] !== router) {
    t4 = (filterKeys, event) => {
      event?.stopPropagation();
      return router.replace({
        pathname: router?.pathname,
        query: deepOmit({
          ...router?.query,
          popover: filterKeys?.length > 1 ? "price" : filterKeys?.[0]
        }, isFilled)
      }, undefined, {
        shallow: true,
        scroll: false
      });
    };
    $[6] = router;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onClick = t4;
  let t5;
  if ($[8] !== router) {
    t5 = (filterKeys_0, event_0) => {
      event_0?.preventDefault();
      event_0?.stopPropagation();
      const currentPopover = String(router?.query?.popover || "");
      const draftKeysToClear = [...filterKeys_0, currentPopover].filter(Boolean);
      if (draftKeysToClear.length > 0) {
        setDraftRangeValues(previous_0 => omit(previous_0, draftKeysToClear) as DraftRangeValues);
      }
      return router.replace({
        pathname: router?.pathname,
        query: deepOmit(omit(router?.query, [...filterKeys_0, "popover"]), isFilled)
      }, undefined, {
        shallow: true,
        scroll: false
      });
    };
    $[8] = router;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const onClose = t5;
  let t6;
  if ($[10] !== onClose || $[11] !== router?.query?.popover) {
    t6 = () => {
      const currentPopover_0 = String(router?.query?.popover || "");
      return onClose([currentPopover_0, ...(currentPopover_0 === "price_range" ? ["price_from", "price_to"] : [])]);
    };
    $[10] = onClose;
    $[11] = router?.query?.popover;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const onReset = t6;
  let t7;
  if ($[13] !== chipsFiltersSchema || $[14] !== draftRangeValues || $[15] !== onClose || $[16] !== router || $[17] !== shouldDeferRangeUpdates) {
    t7 = event_1 => {
      event_1?.preventDefault();
      event_1?.stopPropagation();
      const currentPopover_1 = String(router?.query?.popover || "");
      const schema_0 = chipsFiltersSchema[currentPopover_1];
      if (shouldDeferRangeUpdates && schema_0?.type === "range") {
        const rangeMin = Number(schema_0.min ?? 0);
        const rangeMax = Number(schema_0.max ?? 0);
        const [minValue, maxValue] = draftRangeValues[currentPopover_1] ?? [Number(router.query[schema_0.min_key] || rangeMin), Number(router.query[schema_0.max_key] || rangeMax)];
        setDraftRangeValues(previous_1 => omit(previous_1, [currentPopover_1]) as DraftRangeValues);
        return router.replace({
          pathname: router?.pathname,
          query: deepOmit({
            ...router.query,
            popover: undefined,
            [schema_0.min_key]: Number(minValue) === rangeMin ? undefined : minValue,
            [schema_0.max_key]: Number(maxValue) === rangeMax ? undefined : maxValue
          }, isFilled)
        }, undefined, {
          shallow: true,
          scroll: false
        });
      }
      return onClose([], event_1);
    };
    $[13] = chipsFiltersSchema;
    $[14] = draftRangeValues;
    $[15] = onClose;
    $[16] = router;
    $[17] = shouldDeferRangeUpdates;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const onSubmit = t7;
  let t8;
  if ($[19] !== children || $[20] !== filtersSchemaEntries || $[21] !== onClick || $[22] !== onClose || $[23] !== onReset || $[24] !== onSubmit) {
    t8 = {
      filtersSchemaEntries,
      children,
      onClick,
      onClose,
      onSubmit,
      onReset
    };
    $[19] = children;
    $[20] = filtersSchemaEntries;
    $[21] = onClick;
    $[22] = onClose;
    $[23] = onReset;
    $[24] = onSubmit;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
};